import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HandHeart, ShieldCheck } from "lucide-react";
import { IMAGES } from "./data";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Invitation() {
  const [name, setName] = useState("");
  const [intention, setIntention] = useState("");
  const [sendingIntention, setSendingIntention] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const sendIntention = async (e) => {
    e.preventDefault();
    setSendingIntention(true);
    setFeedback(null);
    try {
      const { data } = await axios.post(`${API}/prayer-intentions`, { name: name || undefined, intention });
      toast.success(data.message);
      setFeedback({ error: false, message: data.message });
      setName("");
      setIntention("");
    } catch {
      const message = "We couldn’t receive your intention. Please try again.";
      toast.error(message);
      setFeedback({ error: true, message });
    } finally {
      setSendingIntention(false);
    }
  };

  return (
    <section id="invitation" data-testid="prayer-intention-section" className="relative overflow-hidden py-20 sm:py-28">
      <div className="absolute inset-0" aria-hidden="true">
        <img src={IMAGES.stainedGlass.url} alt="" className="h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-[linear-gradient(160deg,rgba(16,42,67,0.94),rgba(16,42,67,0.86)_45%,rgba(16,42,67,0.97))]" />
      </div>
      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-8 lg:grid-cols-2 lg:gap-16 lg:px-16">
        <Reveal className="max-w-xl">
          <Eyebrow>An invitation</Eyebrow>
          <h2 data-testid="prayer-intention-heading" className="mt-4 font-serif text-3xl font-medium leading-[1.08] text-ivory sm:text-4xl lg:text-5xl">Pray with Mary, wherever you are</h2>
          <p data-testid="prayer-intention-introduction" className="mt-5 text-base leading-relaxed text-ivory/75 sm:text-lg">Leave an intention, and it will be remembered quietly in prayer.</p>
        </Reveal>
        <Reveal delay={0.08} className="min-w-0">
          <form onSubmit={sendIntention} data-testid="prayer-intention-form" className="rounded-2xl border border-ivory/15 bg-ivory/[0.06] p-6 backdrop-blur-md sm:p-8">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-rose/25 text-rose"><HandHeart className="h-5 w-5" strokeWidth={1.7} aria-hidden="true" /></span>
            <h3 data-testid="prayer-intention-form-heading" className="mt-5 font-serif text-2xl font-semibold text-ivory">Leave an intention</h3>
            <div className="mt-6 space-y-3">
              <label htmlFor="intention-name" className="sr-only">Your name (optional)</label>
              <input id="intention-name" data-testid="intention-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your first name (optional)" className="w-full rounded-full border border-ivory/25 bg-midnight/60 px-5 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold" />
              <label htmlFor="intention-text" className="sr-only">Your prayer intention</label>
              <textarea id="intention-text" data-testid="intention-text-input" required rows={3} maxLength={2000} value={intention} onChange={(e) => setIntention(e.target.value)} placeholder="For whom, or for what, shall we pray?" className="w-full rounded-2xl border border-ivory/25 bg-midnight/60 px-5 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold" />
              <button type="submit" data-testid="intention-submit" disabled={sendingIntention} className="w-full rounded-full bg-rose px-6 py-3 text-sm font-semibold text-ivory transition-[filter] duration-300 hover:brightness-110 disabled:opacity-60">{sendingIntention ? "Sending…" : "Entrust this intention"}</button>
            </div>
            {feedback && <p data-testid="intention-feedback" role={feedback.error ? "alert" : "status"} className="mt-4 text-sm leading-relaxed text-ivory">{feedback.message}</p>}
            <p data-testid="intention-privacy-note" className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-ivory/55"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" />Intentions remain private. They are never published, and no personal or health details are shared without explicit permission.</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}